'use client'

import { Volume2, VolumeX } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

interface YouTubePlayer {
  destroy: () => void
  mute: () => void
  unMute: () => void
  playVideo: () => void
}

declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          height: string
          width: string
          videoId: string
          playerVars?: {
            autoplay?: number
            mute?: number
            controls?: number
            modestbranding?: number
            rel?: number
            playsinline?: number
            loop?: number
            playlist?: string
          }
          events?: {
            onReady?: () => void
          }
        }
      ) => YouTubePlayer
    }
    onYouTubeIframeAPIReady: () => void
  }
}

export const CommunityImpact = () => {
  const [isMuted, setIsMuted] = useState(true)
  const playerRef = useRef<YouTubePlayer>(null)
  const t = useTranslations('community_threads')

  useEffect(() => {
    // Load YouTube IFrame API
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    document.body.appendChild(tag)

    window.onYouTubeIframeAPIReady = () => {
      playerRef.current = new window.YT.Player('community-impact-player', {
        height: '100%',
        width: '100%',
        videoId: 'ica8JqXKZjs',
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          loop: 1,
          playlist: 'ica8JqXKZjs',
        },
        events: {
          onReady: () => {
            playerRef.current?.playVideo()
          },
        },
      })
    }

    return () => {
      if (playerRef.current?.destroy) {
        playerRef.current.destroy()
      }
    }
  }, [])

  const toggleSound = () => {
    if (playerRef.current) {
      if (isMuted) {
        playerRef.current.unMute()
      } else {
        playerRef.current.mute()
      }
      setIsMuted(!isMuted)
    }
  }

  return (
    <section className='relative w-full overflow-hidden'>
      {/* Background layer */}
      <div
        className='absolute inset-x-0 top-0 z-0 h-[75%] md:h-[80%]'
        style={{
          backgroundImage: `url('https://mcdn.coolmate.me/image/2026/03/23/Frame_88081.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      />

      {/* Content */}
      <div className='relative z-10 flex w-full flex-col items-center px-6 pb-0 pt-16 md:px-15 md:pt-24'>
        <div className='mb-10 flex max-w-5xl flex-col items-center gap-1 text-center md:gap-3'>
          <span className='text-sm font-bold uppercase italic tracking-wider text-[#D9FF00] md:text-base'>
            {t('impact.tag')}
          </span>
          <h2 className='font-criteria text-3xl font-bold uppercase italic leading-snug tracking-tight text-white md:text-5xl md:leading-normal'>
            {t('impact.title_line1')} <br className='hidden md:block' />
            {t('impact.title_line2')}
          </h2>
          <p className='mt-2 text-base leading-[1.6] text-[#E6E6E6] md:max-w-3xl md:text-lg'>
            {t('impact.description')}
          </p>
        </div>

        <div className='relative mb-8 aspect-video w-full max-w-[1056px] overflow-hidden rounded-[16px] shadow-2xl md:mb-16 md:rounded-[24px] 2xl:max-w-[1240px]'>
          <div className='absolute inset-0'>
            <div
              id='community-impact-player'
              className='pointer-events-none size-full scale-150'
            />
          </div>
          <button
            onClick={toggleSound}
            className='absolute left-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/50 text-white transition-colors hover:bg-black/70'
            aria-label={isMuted ? t('hero.unmute') : t('hero.mute')}
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>
      </div>
    </section>
  )
}
